import { useEffect, useRef, useState, useCallback } from 'react';
import { ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import type { AlphaTabApi, Settings } from '@coderline/alphatab';
import type { PlaybackState, ScoreTimeline } from '@/types';
import { useNotationCursor } from '@/hooks/useNotationCursor';
import { useNotationScrub } from '@/hooks/useNotationScrub';
import { buildMeasureXMap, type ScrollAnchor } from '@/utils/notationCursor';
import { installNotationCursorSeam } from '@/utils/notationCursorSeam';
import { NOTATION_LOAD_ERROR_TEXT, formatNotationTraceText } from '@/utils/loadErrorText';
import { NOTATION_LOAD_TIMEOUT_MS } from '@/utils/loadTimeouts';
import { usePlaybackStore } from '@/stores';
import type { SkipContext } from '@/stores/playbackStore';

interface NotationDisplayProps {
  scoreId: string;
  musicXmlUrl?: string;
  /** Latest X-Trace-ID for this score (from upload/status-poll), shown in the error state */
  traceId?: string;
  playbackState: PlaybackState;
  timeline: ScoreTimeline | null | undefined;
  width: number;
  height: number;
  resolveSkipContext?: () => SkipContext | null;
}

const MIN_ZOOM = 0.4;
const MAX_ZOOM = 2.0;
const ZOOM_STEP = 0.2;
const DEFAULT_ZOOM = 1.0;
// Defense-in-depth for alphaTab's "polls forever, no error event" font-load failure mode
// (OTO-18 Bug 1) -- bundling the font (see vite.config.ts) fixes the known 404 cause, but
// this bounds any future hang to a visible error instead of an infinite spinner.
const LOAD_TIMEOUT_MS = NOTATION_LOAD_TIMEOUT_MS;

// alphaTab's AlphaTabApi.destroy() can throw (`this._cancelResize is not a function`) when
// called on an instance still mid-render, e.g. torn down by a StrictMode mount/cleanup
// cycle. Uncaught, this propagates out of a useEffect cleanup and (no error boundary in
// this app) unmounts the entire React tree -- isolate it instead of blanking the page.
function safeDestroy(api: AlphaTabApi): void {
  try {
    api.destroy();
  } catch (err) {
    console.error(
      '[NotationDisplay/destroy] Failed: alphaTab api.destroy() | reason=',
      err instanceof Error ? err.message : err
    );
  }
}

export function NotationDisplay({
  scoreId: _scoreId,
  musicXmlUrl,
  traceId,
  playbackState,
  timeline,
  width: _width,
  height: _height,
  resolveSkipContext,
}: NotationDisplayProps) {
  // OTO-21: both subscribed internally (were props from PracticeView).
  // currentTime drives the per-frame cursor scroll via useNotationCursor, so
  // it stays a raw (unquantized) selector -- this panel re-renders per frame
  // like WaterfallDisplay while open. currentMeasure only drives the footer
  // label and already changes at a natural low rate (measure boundaries).
  const currentMeasure = usePlaybackStore((s) => s.position.currentMeasure);
  const currentTime = usePlaybackStore((s) => s.position.currentTime);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const alphaTabContainerRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<AlphaTabApi | null>(null);
  const boundsLookupRef = useRef<NonNullable<AlphaTabApi['boundsLookup']> | null>(null);
  const measureXMapRef = useRef<Map<number, number>>(new Map());
  const beatAnchorsRef = useRef<ScrollAnchor[]>([]);
  const loadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const zoomRef = useRef(DEFAULT_ZOOM);
  const contentWidthRef = useRef(0);
  const [renderVersion, setRenderVersion] = useState(0);

  useEffect(() => {
    const contentEl = alphaTabContainerRef.current;
    if (!contentEl || !timeline) return;

    const map = new Map<number, number>();
    const anchors: ScrollAnchor[] = [];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const lookup = (apiRef.current as any)?.renderer?.boundsLookup ?? boundsLookupRef.current;
    if (lookup) {
      for (const measure of timeline.measures) {
        const bounds = lookup.findMasterBarByIndex(measure.number - 1);
        if (bounds) {
          map.set(measure.number, bounds.realBounds.x);
        }
      }

      // Per-beat cursor anchors (see ScrollAnchor / computeScrollLeft): for
      // each measure, take whichever staff has the finest rhythmic
      // subdivision in that bar (most non-rest beats) as the reference, and
      // place each beat at its rhythmically-correct fraction of the
      // measure's real duration -- `beat.displayStart`/`displayDuration`
      // are already bar-relative midi ticks, so no tempo conversion is
      // needed, only a ratio into `measure.duration`. Wrapped defensively:
      // any unexpected alphaTab data shape just yields no anchors for that
      // measure, and computeScrollLeft falls back to its existing
      // measure-boundary linear interpolation for it.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const staves: any[] = (apiRef.current as any)?.score?.tracks?.[0]?.staves ?? [];
      for (const measure of timeline.measures) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          let bestBeats: any[] = [];
          for (const staff of staves) {
            const bar = staff?.bars?.[measure.number - 1];
            const beats = (bar?.voices?.[0]?.beats ?? []).filter(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (b: any) => !b.isFullBarRest && (b.notes?.length ?? 0) > 0
            );
            if (beats.length > bestBeats.length) bestBeats = beats;
          }
          if (bestBeats.length === 0) continue;

          const barTotalTicks = Math.max(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ...bestBeats.map((b: any) => (b.displayStart ?? 0) + (b.displayDuration ?? 0))
          );
          if (!(barTotalTicks > 0)) continue;

          for (const beat of bestBeats) {
            const beatBounds = lookup.findBeat(beat);
            if (!beatBounds || typeof beatBounds.onNotesX !== 'number') continue;
            const ratio = Math.max(0, Math.min(1, (beat.displayStart ?? 0) / barTotalTicks));
            anchors.push({
              time: measure.startTime + ratio * measure.duration,
              x: beatBounds.onNotesX,
            });
          }
        } catch (err) {
          console.warn(
            `[NotationCursor] Failed: per-beat anchors for measure ${measure.number} | reason=`,
            err instanceof Error ? err.message : err
          );
        }
      }
      anchors.sort((a, b) => a.time - b.time);
    }

    if (map.size === 0) {
      const domMap = buildMeasureXMap(contentEl, zoom);
      domMap.forEach((x, n) => map.set(n, x));
    }

    measureXMapRef.current = map;
    beatAnchorsRef.current = anchors;
  }, [renderVersion, timeline, zoom]);

  const { isDragging } = useNotationScrub({
    scrollContainerRef,
    contentRef: alphaTabContainerRef,
    measureXMapRef,
    beatAnchorsRef,
    timeline,
    resolveSkipContext,
  });

  const { showCursor } = useNotationCursor({
    scrollContainerRef,
    contentRef: alphaTabContainerRef,
    measureXMapRef,
    beatAnchorsRef,
    timeline,
    currentTime,
    playbackState,
    isDragging,
  });

  useEffect(() => {
    installNotationCursorSeam(() => {
      const container = scrollContainerRef.current;
      const contentEl = alphaTabContainerRef.current;
      if (!container || !contentEl || !timeline) return null;
      return {
        measures: timeline.measures,
        totalDuration: timeline.totalDuration,
        measureXMap: Array.from(measureXMapRef.current.entries()),
        beatAnchors: beatAnchorsRef.current,
        contentScrollWidth: contentEl.scrollWidth,
        containerWidth: container.clientWidth,
        scrollLeft: container.scrollLeft,
      };
    });
  }, [timeline]);

  const handleZoomIn = useCallback(() => {
    const next = Math.min(MAX_ZOOM, zoomRef.current + ZOOM_STEP);
    zoomRef.current = next;
    setZoom(next);
  }, []);

  const handleZoomOut = useCallback(() => {
    const next = Math.max(MIN_ZOOM, zoomRef.current - ZOOM_STEP);
    zoomRef.current = next;
    setZoom(next);
  }, []);

  const handleFitToWidth = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || contentWidthRef.current <= 0) return;
    const fit = Math.max(
      MIN_ZOOM,
      Math.min(MAX_ZOOM, container.clientWidth / contentWidthRef.current)
    );
    zoomRef.current = fit;
    setZoom(fit);
  }, []);

  useEffect(() => {
    if (!alphaTabContainerRef.current || !musicXmlUrl) return;

    // Guards against React 18 StrictMode's mount->cleanup->mount double-invoke: without
    // this, the first invocation's async import()/AlphaTabApi construction can resume
    // and race a second AlphaTabApi onto the same (already-cleaned-up) container after
    // cleanup already ran, destroying one instance mid-init and crashing the other on a
    // stale ref. Discovered via real E2E (OTO-18 Task 4), not simulated.
    let cancelled = false;

    const initAlphaTab = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const alphaTab = await import('@coderline/alphatab');
        if (cancelled) return;

        const settings: Settings = new alphaTab.Settings();

        settings.core.engine = 'svg';
        settings.core.logLevel = alphaTab.LogLevel.Warning;
        settings.core.useWorkers = false;
        settings.core.fontDirectory = '/font/alphatab/';

        settings.display.layoutMode = alphaTab.LayoutMode.Horizontal;
        settings.display.staveProfile = alphaTab.StaveProfile.ScoreTab;
        settings.display.scale = 1.0;
        settings.display.stretchForce = 1.0;

        const res = settings.display.resources as unknown as Record<string, unknown>;
        res.staffLineColor = '#555555';
        res.mainGlyphColor = '#1a1a1a';
        res.secondaryGlyphColor = '#444444';
        res.scoreInfoColor = '#333333';
        res.barNumberColor = '#c04040';
        res.barSeparatorColor = '#666666';

        settings.notation.notationMode = alphaTab.NotationMode.SongBook;

        settings.player.enablePlayer = false;
        settings.player.enableCursor = false;
        settings.player.enableUserInteraction = false;

        if (!alphaTabContainerRef.current) return;
        const api = new alphaTab.AlphaTabApi(alphaTabContainerRef.current, settings);
        if (cancelled) {
          safeDestroy(api);
          return;
        }
        apiRef.current = api;

        api.renderStarted.on(() => setIsLoading(true));

        const clearLoadTimeout = () => {
          if (loadTimeoutRef.current) {
            clearTimeout(loadTimeoutRef.current);
            loadTimeoutRef.current = null;
          }
        };

        api.renderFinished.on(() => {
          clearLoadTimeout();
          setIsLoading(false);
          if (alphaTabContainerRef.current) {
            contentWidthRef.current = alphaTabContainerRef.current.scrollWidth;
          }
          boundsLookupRef.current = api.boundsLookup;
          setRenderVersion((v) => v + 1);
        });

        api.error.on((e) => {
          clearLoadTimeout();
          console.error(
            '[NotationDisplay/render] Failed: alphaTab error | reason=',
            e.message || e
          );
          setError(e.message || 'Failed to render notation');
          setIsLoading(false);
        });

        api.load(musicXmlUrl);

        loadTimeoutRef.current = setTimeout(() => {
          console.error(
            '[NotationDisplay/render] Failed: load timed out | reason=no renderFinished/error ' +
              `event within ${LOAD_TIMEOUT_MS}ms`
          );
          setError('Notation failed to load (timed out)');
          setIsLoading(false);
        }, LOAD_TIMEOUT_MS);
      } catch (err) {
        console.error(
          '[NotationDisplay/init] Failed: alphaTab init | reason=',
          err instanceof Error ? err.message : err
        );
        setError(err instanceof Error ? err.message : 'Failed to initialize notation display');
        setIsLoading(false);
      }
    };

    initAlphaTab();

    return () => {
      cancelled = true;
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
        loadTimeoutRef.current = null;
      }
      if (apiRef.current) {
        safeDestroy(apiRef.current);
        apiRef.current = null;
      }
    };
  }, [musicXmlUrl]);

  if (!musicXmlUrl) {
    return (
      <div className="w-full h-full flex items-center justify-center text-gray-500">
        <p>No notation available</p>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full overflow-hidden bg-gray-100 rounded">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100/80 z-10">
          <div className="text-center">
            <div className="loading-spinner mx-auto mb-2" />
            <p className="text-sm text-gray-500">Loading notation...</p>
          </div>
        </div>
      )}

      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100/80 z-10">
          <div className="text-center text-red-600">
            <p className="mb-2">{NOTATION_LOAD_ERROR_TEXT}</p>
            <p className="text-sm text-gray-500">{error}</p>
            {traceId && (
              <p className="mt-1 text-xs text-gray-400 font-mono select-all">
                {formatNotationTraceText(traceId)}
              </p>
            )}
          </div>
        </div>
      )}

      {showCursor && (
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-red-500/80 pointer-events-none z-30"
          style={{ left: '25%' }}
        />
      )}

      <div className="absolute top-2 right-2 z-20 flex items-center gap-1 bg-white/90 rounded-lg shadow px-1 py-0.5">
        <button
          onClick={handleZoomOut}
          disabled={zoom <= MIN_ZOOM}
          className="p-1 hover:bg-gray-200 rounded disabled:opacity-30 transition-colors"
          title="Zoom out"
        >
          <ZoomOut className="w-4 h-4 text-gray-700" />
        </button>
        <span className="text-xs text-gray-600 w-10 text-center font-mono">
          {Math.round(zoom * 100)}%
        </span>
        <button
          onClick={handleZoomIn}
          disabled={zoom >= MAX_ZOOM}
          className="p-1 hover:bg-gray-200 rounded disabled:opacity-30 transition-colors"
          title="Zoom in"
        >
          <ZoomIn className="w-4 h-4 text-gray-700" />
        </button>
        <div className="w-px h-4 bg-gray-300 mx-0.5" />
        <button
          onClick={handleFitToWidth}
          className="p-1 hover:bg-gray-200 rounded transition-colors"
          title="Fit to width"
        >
          <Maximize2 className="w-4 h-4 text-gray-700" />
        </button>
      </div>

      <div ref={scrollContainerRef} className="w-full h-full overflow-x-auto overflow-y-auto">
        <div
          ref={alphaTabContainerRef}
          data-testid="notation-surface"
          style={{
            transform: `scale(${zoom})`,
            transformOrigin: 'top left',
            width: `${100 / zoom}%`,
          }}
        />
      </div>

      <div className="absolute bottom-2 right-2 bg-white/80 px-2 py-1 rounded text-xs text-gray-600 shadow-sm">
        Measure {currentMeasure}
      </div>
    </div>
  );
}
